import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChefHat, HeartHandshake, Sparkles, Leaf, MousePointer2, ClipboardList, Share2, PartyPopper, Utensils } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import DishImage from "@/components/DishImage";
import HeroSlider from "@/components/HeroSlider";

const HERO = "https://images.pexels.com/photos/8818732/pexels-photo-8818732.jpeg";
const PLATE = "https://images.pexels.com/photos/17223837/pexels-photo-17223837.jpeg?auto=compress&cs=tinysrgb&w=1000";

const serviceCards = [
  { title: "Weddings", img: "https://images.unsplash.com/photo-1680993032090-1ef7ea9b51e5?w=1000&q=75&auto=format&fit=crop", tag: "The Grand Day" },
  { title: "Corporate", img: "https://images.unsplash.com/photo-1559561724-732dbca7be1e?w=1000&q=75&auto=format&fit=crop", tag: "Professional" },
  { title: "Private Parties", img: "https://images.pexels.com/photos/36885725/pexels-photo-36885725.jpeg?auto=compress&cs=tinysrgb&w=1000", tag: "Intimate" },
];

const StructuredData = () => {
  useEffect(() => {
    const ld = {
      "@context": "https://schema.org", "@type": "LocalBusiness", name: "Mayuresh Caterers", image: HERO,
      "@id": window.location.origin, url: window.location.origin, telephone: "+91-98765-43210",
      address: { "@type": "PostalAddress", streetAddress: "Market Road, Roha", addressLocality: "Roha", addressRegion: "Raigad, Maharashtra", postalCode: "402116", addressCountry: "IN" },
      geo: { "@type": "GeoCoordinates", latitude: "18.4370", longitude: "73.1200" },
      servesCuisine: "Indian, Maharashtrian", priceRange: "₹₹", areaServed: "Roha, Raigad",
    };
    let el = document.getElementById("ld-localbusiness");
    if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = "ld-localbusiness"; document.head.appendChild(el); }
    el.textContent = JSON.stringify(ld);
  }, []);
  return null;
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.2, 0.7, 0.2, 1] } }),
};

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/dishes").then((r) => setFeatured(r.data.filter((d) => d.featured).slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="overflow-hidden">
      <Seo
        title="Mayuresh Caterers — Wedding & Event Catering in Roha, Raigad (402116)"
        description="Premium wedding, corporate & private catering in Roha, Raigad. Build your custom menu online and request a quote from Mayuresh Caterers."
      />
      <StructuredData />

      {/* HERO SLIDER */}
      <HeroSlider />

      {/* MARQUEE */}
      <section className="bg-[#1A1A1A] py-5 border-y border-white/10">
        <div className="marquee-track">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-10 pr-10">
              {["Weddings", "Corporate Events", "Maharashtrian Thali", "Live Counters", "Jain Menus", "Private Parties", "Festive Feasts"].map((w) => (
                <span key={w} className="flex items-center gap-10 serif text-2xl text-white/40 italic">
                  {w} <span className="text-[#C9A66B] not-italic">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* INTRO / VALUE PROPS */}
      <section className="max-w-7xl mx-auto px-5 py-28">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-16 items-start">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <p className="label-tag with-rule text-[#B08D4F] mb-5">Why Mayuresh</p>
            <h2 className="text-5xl sm:text-6xl font-medium text-[#1A1A1A] display-title">
              Food that feels like <span className="italic text-gold-grad">home</span>, served like a feast
            </h2>
            <p className="text-[#5b554b] mt-6 leading-relaxed text-lg max-w-md">
              For over fifteen years we've plated celebrations across Roha and Raigad — blending time-honoured recipes with sober, premium presentation.
            </p>
            <Link to="/services" className="link-underline inline-flex items-center gap-2 mt-8 text-[#1A1A1A] font-medium">
              Explore our services <ArrowRight size={16} className="text-[#B08D4F]" />
            </Link>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { icon: ChefHat, t: "Crafted Kitchens", d: "Fresh, seasoned cooking for every event.", n: "01" },
              { icon: HeartHandshake, t: "Personal Service", d: "One dedicated team, start to finish.", n: "02" },
              { icon: Sparkles, t: "Elegant Plating", d: "Sober, premium table settings.", n: "03" },
            ].map((v, i) => (
              <motion.div key={v.t} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className="card-lux rounded-sm p-7 flex flex-col">
                <div className="flex items-center justify-between mb-6">
                  <v.icon className="text-[#C9A66B]" size={26} />
                  <span className="serif text-3xl text-[#E5DCC9]">{v.n}</span>
                </div>
                <h3 className="text-2xl font-semibold text-[#1A1A1A] mb-2">{v.t}</h3>
                <p className="text-[#5b554b] text-sm leading-relaxed">{v.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED DISHES */}
      <section className="bg-[#1A1A1A] py-28 relative">
        <div className="max-w-7xl mx-auto px-5">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
            <div>
              <p className="label-tag with-rule text-[#C9A66B] mb-4">Signature Selections</p>
              <h2 className="text-white text-5xl sm:text-6xl font-medium display-title">Featured Dishes</h2>
            </div>
            <Link to="/build" className="link-underline text-[#C9A66B] flex items-center gap-2 text-sm">
              Explore full menu <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((d, i) => (
              <motion.div key={d.id} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className="group relative overflow-hidden rounded-sm" data-testid={`featured-dish-${d.id}`}>
                <div className="aspect-[3/4] overflow-hidden">
                  <DishImage src={d.image} alt={d.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <p className="label-tag text-[#E4CE9E] text-[0.58rem] mb-1">{d.category}</p>
                  <h4 className="text-white text-2xl font-medium leading-tight">{d.name}</h4>
                  {d.dietary?.includes("veg") && (
                    <span className="inline-flex items-center gap-1 text-[#9bd177] text-xs mt-2"><Leaf size={12} /> Pure Veg</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-5 py-28">
        <div className="text-center mb-14">
          <p className="label-tag text-[#B08D4F] mb-4">What We Cater</p>
          <h2 className="text-5xl sm:text-6xl font-medium text-[#1A1A1A] display-title">For every occasion</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {serviceCards.map((s, i) => (
            <motion.div key={s.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
              <Link to="/services" className="group block relative overflow-hidden rounded-sm h-[420px]">
                <img src={s.img} alt={`${s.title} catering`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <p className="label-tag text-[#E4CE9E] text-[0.58rem] mb-2">{s.tag}</p>
                  <h3 className="text-white text-3xl font-medium flex items-center gap-3">
                    {s.title}
                    <ArrowRight size={20} className="text-[#C9A66B] -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
                  </h3>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#F1EADB] py-28">
        <div className="max-w-7xl mx-auto px-5">
          <div className="text-center mb-16">
            <p className="label-tag text-[#B08D4F] mb-4">Simple & Delightful</p>
            <h2 className="text-5xl sm:text-6xl font-medium text-[#1A1A1A] display-title">Your menu, in four steps</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: MousePointer2, t: "Pick Occasion", d: "Choose your event and guest count." },
              { icon: Utensils, t: "Compose Plate", d: "Add dishes to a live thali." },
              { icon: ClipboardList, t: "Review Menu", d: "See your full spread at a glance." },
              { icon: Share2, t: "Share & Enquire", d: "Export, WhatsApp or send to us." },
            ].map((p, i) => (
              <motion.div key={p.t} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className="relative pl-4">
                <div className="absolute left-0 top-1 bottom-1 w-[2px] bg-[#C9A66B]/40" />
                <p.icon className="text-[#B08D4F] mb-4" size={28} />
                <span className="serif text-2xl text-[#C9A66B]">0{i + 1}</span>
                <h3 className="text-2xl font-semibold text-[#1A1A1A] mt-1">{p.t}</h3>
                <p className="text-[#5b554b] text-sm mt-2 leading-relaxed">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 py-28">
        <div className="relative bg-[#1A1A1A] rounded-sm overflow-hidden grid lg:grid-cols-2 items-center">
          <div className="p-10 sm:p-16 relative z-10">
            <PartyPopper className="text-[#C9A66B] mb-5" size={32} />
            <h2 className="text-4xl sm:text-5xl font-medium text-white display-title">Design your menu in minutes</h2>
            <p className="text-white/60 mt-5 leading-relaxed max-w-md">
              Pick your occasion, choose dishes, and watch them arrange on a lifelike thali. Export it, share on WhatsApp, or send it straight to us.
            </p>
            <Link to="/build" className="btn-gold px-8 py-4 rounded-sm inline-flex items-center gap-2 text-sm mt-9" data-testid="cta-strip-build">
              Start Building <ArrowRight size={18} />
            </Link>
          </div>
          <div className="relative h-64 lg:h-full min-h-[340px]">
            <img src={PLATE} alt="Beautifully plated Indian catering dish" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A] via-[#1A1A1A]/20 to-transparent lg:block hidden" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] to-transparent lg:hidden" />
          </div>
        </div>
      </section>
    </div>
  );
}
